import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, UserPlus, Search, Eye, Trash2, Star, Phone, Mail,
  MapPin, Calendar, Briefcase, FileText, Building2, FolderOpen, MessageSquare, Settings,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminEmploymentPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    fetchApplications();
  }, [navigate]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employment`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, rating = null, notes = null) => {
    try {
      const updateData = { status };
      if (rating !== null) updateData.rating = rating;
      if (notes !== null) updateData.notes = notes;
      
      await axios.put(`${BACKEND_URL}/api/employment/${id}/status`, updateData);
      fetchApplications();
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, status, rating, notes });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/employment/${id}`);
      fetchApplications();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const filteredApplications = useMemo(() => {
    let result = applications.filter(app => {
      const matchesSearch = 
        `${app.first_name} ${app.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    // Sort the results
    result.sort((a, b) => {
      let aVal, bVal;
      
      if (sortField === 'name') {
        aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
        bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else if (sortField === 'created_at') {
        aVal = new Date(a[sortField]);
        bVal = new Date(b[sortField]);
      } else if (sortField === 'experience_years' || sortField === 'rating') {
        aVal = parseInt(a[sortField]) || 0;
        bVal = parseInt(b[sortField]) || 0;
      } else {
        aVal = (a[sortField] || '').toString().toLowerCase();
        bVal = (b[sortField] || '').toString().toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [applications, searchTerm, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-slate-300" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-cyan-600" /> 
      : <ArrowDown className="w-4 h-4 text-cyan-600" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      reviewing: 'bg-yellow-100 text-yellow-800',
      interviewed: 'bg-purple-100 text-purple-800',
      offered: 'bg-cyan-100 text-cyan-800',
      hired: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const statusCounts = {
    all: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    reviewing: applications.filter(a => a.status === 'reviewing').length,
    interviewed: applications.filter(a => a.status === 'interviewed').length,
    offered: applications.filter(a => a.status === 'offered').length,
    hired: applications.filter(a => a.status === 'hired').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  return (
    <AdminSidebar pageTitle="Employment Applications" pageIcon={UserPlus}>
      {/* Header Actions */}
      <div className="flex justify-end mb-6">
        <Link to="/admin/jobs">
          <Button className="bg-cyan-600 hover:bg-cyan-700">
            <Briefcase className="w-4 h-4 mr-2" />
            Manage Job Postings
          </Button>
        </Link>
      </div>

      {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {Object.entries(statusCounts).map(([status, count]) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`p-3 rounded-xl text-center transition-all ${
                  statusFilter === status 
                    ? 'bg-cyan-600 text-white shadow-lg' 
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <p className="text-xl font-bold">{count}</p>
                <p className="text-xs capitalize">{status}</p>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, position, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Applications List */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-600 mt-4">Loading applications...</p>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No employment applications found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-cyan-600">
                          Applicant <SortIcon field="name" />
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <button onClick={() => handleSort('position')} className="flex items-center gap-1 hover:text-cyan-600">
                          Position <SortIcon field="position" />
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <button onClick={() => handleSort('experience_years')} className="flex items-center gap-1 hover:text-cyan-600">
                          Experience <SortIcon field="experience_years" />
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-cyan-600">
                          Status <SortIcon field="status" />
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <button onClick={() => handleSort('rating')} className="flex items-center gap-1 hover:text-cyan-600">
                          Rating <SortIcon field="rating" />
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">
                        <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-cyan-600">
                          Date <SortIcon field="created_at" />
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <p className="font-medium text-slate-900">{app.first_name} {app.last_name}</p>
                          <p className="text-sm text-slate-500">{app.email}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-slate-700">{app.position}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-slate-700">{app.experience_years} years</p>
                        </td>
                        <td className="p-4">{getStatusBadge(app.status)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            {[1,2,3,4,5].map(star => (
                              <Star 
                                key={star} 
                                className={`w-4 h-4 ${star <= (app.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(app.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedApplication(app); setShowDetailModal(true); }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteApplication(app.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

      {/* Detail Modal */}
      {showDetailModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">
                  {selectedApplication.first_name} {selectedApplication.last_name}
                </h2>
                <Button variant="ghost" onClick={() => setShowDetailModal(false)}>×</Button>
              </div>
              <p className="text-slate-600">{selectedApplication.position}</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${selectedApplication.email}`} className="text-cyan-600">
                    {selectedApplication.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${selectedApplication.phone}`} className="text-cyan-600">
                    {selectedApplication.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>{selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>Available: {selectedApplication.start_date}</span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Experience</p>
                  <p className="font-medium">{selectedApplication.experience_years} years</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Desired Pay</p>
                  <p className="font-medium">{selectedApplication.desired_pay || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Previous Employer</p>
                  <p className="font-medium">{selectedApplication.previous_employer || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Previous Position</p>
                  <p className="font-medium">{selectedApplication.previous_position || 'Not specified'}</p>
                </div>
              </div>

              {/* Skills */}
              <div>
                <p className="text-sm text-slate-500 mb-2">Skills</p>
                <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedApplication.skills}</p>
              </div>

              {/* About */}
              <div>
                <p className="text-sm text-slate-500 mb-2">About Themselves</p>
                <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedApplication.about_yourself}</p>
              </div>

              {/* References */}
              {selectedApplication.references && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">References</p>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedApplication.references}</p>
                </div>
              )}

              {/* Rating */}
              <div className="border-t pt-6">
                <p className="text-sm text-slate-500 mb-3">Rate this Applicant</p>
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      onClick={() => updateStatus(selectedApplication.id, selectedApplication.status, star)}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star 
                        className={`w-8 h-8 ${star <= (selectedApplication.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Update */}
              <div className="border-t pt-6">
                <p className="text-sm text-slate-500 mb-3">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {['new', 'reviewing', 'interviewed', 'offered', 'hired', 'rejected'].map(status => (
                    <Button
                      key={status}
                      variant={selectedApplication.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateStatus(selectedApplication.id, status)}
                      className={selectedApplication.status === status ? 'bg-cyan-600' : ''}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminSidebar>
  );
};

export default AdminEmploymentPage;
